import {
    faCalendarDay,
    faCircleHalfStroke,
    faEarthAmericas,
    faObjectsColumn,
} from "@fortawesome/pro-light-svg-icons";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { useAppSelector } from "../../../../hooks";
import { ToggleButton } from "../../ui/MapControlsV2";
import { ControlContainer } from "../common";
import { Insights } from "../Insights";
import { AreasFilterControl } from "./AreasFilterControl";
import { BasemapControls } from "./BasemapControl";
import { DateRangeControl } from "./DateRangeControl";
import { EmissionsControl } from "./EmissionsControl";
import { GoToLocationControl } from "./GoToLocationControl";
import { InfrastructureControl } from "./InfrastructureControl";
import { LayersControl } from "./LayersControl";
import { PlumeControl } from "./PlumeControl";
import { ShareMapControl } from "./ShareControl";

const showInsightsAtom = atomWithStorage("showInsights", true);
const showPlumeControlAtom = atomWithStorage("showPlumeControl", false);
const showAreasControlAtom = atomWithStorage("showAreasControl", false);
const showDatesControlAtom = atomWithStorage("showDatesControl", false);

export const DashboardControls = () => {
    const [showInsights, setShowInsights] = useAtom(showInsightsAtom);
    const [showPlumeControl, setShowPlumeControl] =
        useAtom(showPlumeControlAtom);
    const [showAreasControl, setShowAreasControl] =
        useAtom(showAreasControlAtom);
    const [showDatesControl, setShowDatesControl] =
        useAtom(showDatesControlAtom);

    // Heatmaps enabled?
    const heatmapsEnabled = useAppSelector((s) =>
        s.auth.flags.includes("enable_experimental_heatmaps"),
    );

    return (
        <div className="w-fit p-2 h-full flex flex-col">
            <div className="flex flex-col h-full w-full">
                {/* Toolbar */}
                <div className="flex items-start gap-2 mb-1 flex-shrink-0">
                    <ControlContainer className="p-0.5 w-fit">
                        <div className="flex items-center border border-ae-gray-250 rounded">
                            <ToggleButton
                                enabled={true}
                                icon={faObjectsColumn}
                                label="Insights"
                                selected={showInsights}
                                onClick={() => setShowInsights((i) => !i)}
                                className="rounded-l"
                            />
                            <ToggleButton
                                enabled={true}
                                icon={faEarthAmericas}
                                label="Areas"
                                selected={showAreasControl}
                                onClick={() => setShowAreasControl((i) => !i)}
                            />
                            <ToggleButton
                                enabled={true}
                                icon={faCalendarDay}
                                label="Dates"
                                selected={showDatesControl}
                                onClick={() => setShowDatesControl((i) => !i)}
                            />
                            <EmissionsControl />
                            <InfrastructureControl />
                            {heatmapsEnabled && (
                                <LayersControl className="rounded-r" />
                            )}
                        </div>
                    </ControlContainer>
                    <ControlContainer className="p-0.5">
                        <div className="flex border border-ae-gray-250 rounded">
                            <ToggleButton
                                enabled={true}
                                icon={faCircleHalfStroke}
                                label="Plumes"
                                selected={showPlumeControl}
                                onClick={() => setShowPlumeControl((i) => !i)}
                                className="rounded-l"
                            />
                            <GoToLocationControl />
                            <BasemapControls />
                            <ShareMapControl className="rounded-r" />
                        </div>
                    </ControlContainer>
                    {/* Plume control */}
                    {showPlumeControl && (
                        <ControlContainer className="p-0.5">
                            <PlumeControl />
                        </ControlContainer>
                    )}
                </div>
                {/* Content and expanding controls */}
                <div className="min-h-0 h-fit flex flex-col pointer-events-none gap-1">
                    {/* Areas control */}
                    {showAreasControl && <AreasFilterControl />}
                    {/* Dates control */}
                    {showDatesControl && <DateRangeControl />}
                    {/* Displaying insights and context information */}
                    <Insights showInsights={showInsights} />
                </div>
            </div>
        </div>
    );
};
